import { Box, CircularProgress, Grid, LinearProgress, Popover } from '@material-ui/core';
import {
  Button,
  Checkbox,
  Dialog,
  FormControlLabel,
  Icons,
  Tooltip,
  Typography,
} from '@nutrien/cxp-components';
import localforage from 'localforage';
import * as markerjs2 from 'markerjs2';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';

import type { DeficiencyFiles } from '../../rxdb/deficiencyFiles/deficiencyFiles.types';
import { useFileStorage } from '../../utilities/useFileStorage';
import { useNotification } from '../../utilities/useNotification';
import { useOnlineStatus } from '../../utilities/useOnlineStatus';
import {
  COPY,
  DELETE,
  EDIT_DETAILS,
  XML_HEADER,
  YYYYMMDD_DATE_FORMAT,
} from '../../utilities/constants';
import {
  checkIfTokenExpired,
  downloadURI,
  getFileDataBasedOnType,
  getFileExtension,
  getFileOrder,
  groupArrayByKey,
} from '../../utilities/utilityFunctions';
import AspectRatio from '../AspectRatio';
import { useStyles } from './Thumbnail.styles';
const { MARKER_JS_2_KEY } = process.env;
import dayjs from 'dayjs';
import mime from 'mime-db';
import { v4 as uuidv4 } from 'uuid';

import { useMst } from '../../mobx-models/Root';
import {
  AimsFile,
  DeficiencyFileEdited,
  FileOperation,
  InspectionFilesEdited,
} from '../../models/models';
import { useInspectionFiles } from '../../rxdb/inspectionFiles/useInspectionFiles';
import { isJestTest } from '../../test-helpers/isJestTest';
import { FileCategory } from '../../utilities/enums';
import EditFileDetails from '../EditFileDetails/EditFileDetails';
import OfflineDialog from '../OfflineDialog';
const editedBlobFileStore = localforage.createInstance({
  name: 'edited-blob-file-store',
});

interface Props {
  fileData: DeficiencyFileEdited | InspectionFilesEdited;
  fileList: DeficiencyFiles[] | InspectionFilesEdited[];
  index: number;
  isShowOnReport: boolean;
  updateFileList: (updatedFileList: DeficiencyFileEdited[]) => void;
  arrayListLength: number | undefined;
  setDragDisable: (value: boolean) => void;
  isEditable: boolean;
  showInfoMessage: (value: boolean) => void;
  isActiveDeficiency: boolean;
}

const Thumbnail: React.FC<Props> = ({
  fileData,
  fileList,
  index,
  isShowOnReport,
  updateFileList,
  arrayListLength,
  setDragDisable,
  isEditable,
  showInfoMessage,
  isActiveDeficiency,
}: Props) => {
  const classes = useStyles();
  const { UploadFileProgress } = useMst();
  const imgRef = React.createRef<HTMLImageElement>();
  const [fileUrl, setFileUrl] = useState<string>('');
  const {
    getAttachmentFromIndexDB,
    uploadInspectionFile,
    getImage,
    deleteFileFromAttachmentStore,
    retryFileDownload,
  } = useFileStorage();
  const { updateInspectionFile, deleteInspectionFile } = useInspectionFiles();
  const [hideImage, setHideImage] = useState(true);
  const [openViewImage, setOpenViewImage] = useState<boolean>(false);
  const [openEditDetails, setOpenEditDetails] = useState<boolean>(false);
  const [openFileDelete, setOpenFileDelete] = useState<boolean>(false);
  const [openCopy, setOpenCopy] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<HTMLInputElement | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [offline, setOffline] = useState<boolean>(false);
  const [fileDownloadError, setFileDownloadError] = useState<boolean>(false);
  const [fileNotFound, setFileNotFound] = useState<boolean>(false);
  const online = useOnlineStatus();
  const { defaultNotification } = useNotification();
  const isImage =
    fileData.originalFileName?.toLowerCase().endsWith('.jpg') ||
    fileData.originalFileName?.toLowerCase().endsWith('.jpeg') ||
    fileData.originalFileName?.toLowerCase().endsWith('.png') ||
    fileData.extension === 'image/jpeg' ||
    fileData.extension === 'image/jpg' ||
    fileData.extension === 'image/png';
  const customFileData = {
    fileAimsUuid:
      fileData.fileCategory === FileCategory.DEFICIENCY_FILE
        ? (fileData as DeficiencyFileEdited).fileAimsUUID
        : (fileData as InspectionFilesEdited).fileUUID,
    fileName:
      fileData.fileCategory === FileCategory.DEFICIENCY_FILE
        ? (fileData as DeficiencyFileEdited).fileName
        : (fileData as InspectionFilesEdited).name,
    originalPath:
      fileData.fileCategory === FileCategory.DEFICIENCY_FILE
        ? (fileData as DeficiencyFileEdited).path
        : (fileData as InspectionFilesEdited).originalPath,
    isActiveDeficiencyFile:
      ('isActiveDeficiencyFile' in fileData && fileData.isActiveDeficiencyFile) ||
      isActiveDeficiency,
  };
  const id = customFileData.fileAimsUuid;
  const n = customFileData.fileName.lastIndexOf('.');
  const fileExtension = customFileData.fileName.substring(n + 1)?.toLowerCase();
  const openPopOver = Boolean(anchorEl);
  const idForPopover = openPopOver ? id : undefined;

  const isNotOnDemandSync = () => {
    return (
      !fileData.extension.includes('video') &&
      ((fileData.sizeMb < 100000000 && !fileData.extension.includes('image')) ||
        fileData.extension.includes('image'))
    );
  };

  useEffect(() => {
    if (showInfoMessage) showInfoMessage(!fileUrl && !online);
  }, [fileUrl]);

  const setFileUrlFromDb = async (fileUuid: string) => {
    let blobFromDb: Blob;
    if (
      ((fileData as DeficiencyFileEdited)?.operation === FileOperation.fileCopy ||
        (fileData as DeficiencyFileEdited)?.operation === FileOperation.fileBlobEdited) &&
      fileData.fileCategory === FileCategory.DEFICIENCY_FILE
    ) {
      blobFromDb = (await editedBlobFileStore.getItem(fileUuid)) as Blob;
    } else {
      blobFromDb = (await getAttachmentFromIndexDB(fileUuid)) as Blob;
    }
    if (!blobFromDb || blobFromDb.type === XML_HEADER) {
      setFileNotFound(false);
      if (!online) {
        setFileNotFound(true);
      } else {
        setFileDownloadError(true);
        const response = await retryFileDownload(
          customFileData.originalPath,
          fileUuid,
          true,
          customFileData.isActiveDeficiencyFile,
        );
        setFileDownloadError(false);
        if (await checkIfTokenExpired()) {
          let updateTimeoutSeconds = 10;
          defaultNotification(
            `Session Expired, refreshing session in ${updateTimeoutSeconds} seconds`,
          );
          const getTimer = () => {
            (
              document.getElementById('client-snackbar') as HTMLElement
            ).innerHTML = `Session Expired, refreshing session in ${updateTimeoutSeconds} seconds`;
            if (updateTimeoutSeconds > 0) {
              setTimeout(getTimer, 500);
            }
            updateTimeoutSeconds--;
          };
          setTimeout(() => {
            window.location.reload();
          }, updateTimeoutSeconds * 500);
          getTimer();
          localStorage.setItem('TEST', 'false');
        }
        if (response.type === XML_HEADER) {
          setFileNotFound(true);
        } else {
          setFileUrlFromDb(fileUuid);
        }
      }
    } else {
      setFileUrl(URL.createObjectURL(blobFromDb));
    }
  };

  const editImageFile = async (dataURL: string, fileName: string): Promise<File> => {
    const blobUrl = await (await fetch(dataURL)).blob();
    const updatedBlobFile: File = new File([blobUrl], `${fileName}`, {
      type: 'image/jpeg',
    });
    return updatedBlobFile;
  };

  const autoVersionIfFileExists = (fileName: string, fileIndex = 0): string => {
    let newName = fileName,
      ext = '';
    if (fileIndex) {
      if (newName.indexOf('.') > -1) {
        const tokens = newName.split('.');
        ext = `.${tokens.pop()}`;
        newName = tokens.join('.');
      }
      newName = `${newName} (${fileIndex})${ext}`;
    }

    const nameExists = customFileData.fileName === newName;

    return nameExists ? autoVersionIfFileExists(fileName, fileIndex + 1) : newName;
  };

  const changeImageUrl = async (val: string) => {
    if (openCopy) {
      const editedBlobFile: File = await editImageFile(
        val,
        autoVersionIfFileExists(customFileData.fileName),
      );
      const copyFileKey = uuidv4();
      switch (fileData.fileCategory) {
        case FileCategory.DEFICIENCY_FILE:
          let name = editedBlobFile?.name;
          let ext = getFileExtension(editedBlobFile.name);
          if (ext === undefined) {
            ext = mime[editedBlobFile.type]?.extensions[0];
            name = editedBlobFile.name + '.' + ext;
          }

          await editedBlobFileStore.setItem(copyFileKey, editedBlobFile);
          const fileCopyDetails: DeficiencyFileEdited = JSON.parse(
            JSON.stringify(fileData as DeficiencyFileEdited),
          );
          fileCopyDetails.fileAimsUUID = copyFileKey;
          fileCopyDetails.fileName = name;
          fileCopyDetails.fileSortOrder = arrayListLength;
          fileCopyDetails.showOnReport = false;
          fileCopyDetails.operation = FileOperation.fileCopy;
          const updatedFileList: DeficiencyFileEdited[] = [
            ...JSON.parse(JSON.stringify(fileList)),
            fileCopyDetails,
          ];
          updateFileList(updatedFileList);
          break;
        case FileCategory.INSPECTION_FILE:
          const inspectionFileData: InspectionFilesEdited = JSON.parse(JSON.stringify(fileData));
          inspectionFileData.operation = FileOperation.fileCopy;
          inspectionFileData.fileSortOrder = arrayListLength;
          inspectionFileData.showOnReport = false;
          uploadInspectionFile(editedBlobFile as AimsFile, inspectionFileData);
      }
      setOpenCopy(false);
    } else {
      const editedBlobFile: File = await editImageFile(val, customFileData.fileName);
      switch (fileData.fileCategory) {
        case FileCategory.DEFICIENCY_FILE:
          await editedBlobFileStore.setItem(customFileData.fileAimsUuid, editedBlobFile);
          const updatedFileList: DeficiencyFileEdited[] = JSON.parse(JSON.stringify(fileList));
          updatedFileList[index].operation = FileOperation.fileBlobEdited;
          updateFileList(updatedFileList);
          break;
        case FileCategory.INSPECTION_FILE:
          const inspectionFileData: InspectionFilesEdited = JSON.parse(JSON.stringify(fileData));
          inspectionFileData.operation = FileOperation.fileBlobEdited;
          uploadInspectionFile(editedBlobFile as AimsFile, inspectionFileData);
      }
    }
  };

  const showMarkerArea = () => {
    setDragDisable(true);
    if (imgRef.current !== null) {
      const markerArea = new markerjs2.MarkerArea(imgRef.current);
      markerArea.settings.displayMode = 'popup';
      markerArea.uiStyleSettings.toolbarHeight = 60;
      markerArea.uiStyleSettings.canvasBackgroundColor = '#111111';
      markerArea.uiStyleSettings.toolboxBackgroundColor = 'black';
      if (MARKER_JS_2_KEY && !markerjs2.Activator.isLicensed) {
        markerjs2.Activator.addKey(MARKER_JS_2_KEY);
      }
      const overlayImage: HTMLElement | null = document.getElementById('markerOverlay');
      if (overlayImage) {
        markerArea.targetRoot = overlayImage;
      }
      markerArea.settings.defaultColorSet = ['#e8e8e8', '#cd4f5f', '#ffd966', '#226ea6'];
      markerArea.addRenderEventListener(dataUrl => {
        setHideImage(true);
        if (imgRef.current) {
          imgRef.current.src = dataUrl;
          changeImageUrl(dataUrl);
        }
        changeImageUrl(dataUrl);
      });
      markerArea.addCloseEventListener(() => {
        setHideImage(true);
        setDragDisable(false);
        setOpenCopy(false);
      });
      markerArea.show();
      setHideImage(false);
      setDragDisable(true);
    }
  };

  const handlePopOverOpen = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    setAnchorEl(event.currentTarget as HTMLInputElement);
  };

  const handlePopOverClose = () => {
    setAnchorEl(null);
  };

  const handleEditDetailsOpen = () => {
    setOpenEditDetails(true);
    handlePopOverClose();
    document.body.style.overflow = 'hidden';
    if (!isJestTest()) {
      setTimeout(() => {
        const scrollPosition = document?.querySelector('.scrollClass')?.scrollTop;
        const pos = scrollPosition ? scrollPosition : 0;
        document?.querySelector('.scrollClass')?.scrollBy(pos, 0);
      }, 2000);
    }
  };

  const handleEditDetailsClose = () => {
    setOpenEditDetails(false);
  };

  const handleDeleteOpen = () => {
    setAnchorEl(null);
    setOpenFileDelete(true);
    document.body.style.overflow = 'hidden';
  };

  const handleFileDelete = async () => {
    switch (fileData.fileCategory) {
      case FileCategory.DEFICIENCY_FILE:
        const updatedFileList: DeficiencyFileEdited[] = JSON.parse(JSON.stringify(fileList));
        updatedFileList[index].operation = FileOperation.fileDeleted;
        updatedFileList[index].isDelete = 'true';
        updateFileList(updatedFileList);
        break;
      case FileCategory.INSPECTION_FILE:
        await deleteInspectionFile(customFileData.fileAimsUuid, 'true', fileData.fileSortOrder);
        await deleteFileFromAttachmentStore(customFileData.fileAimsUuid);
    }
    setOpenFileDelete(false);
  };

  const handleShowOnReportBox = (reportChecked: boolean) => {
    let checked = reportChecked;
    let order;
    const defFilePayloadFiles = groupArrayByKey(fileList, 'showOnReport');
    const listOfTrueFiles: DeficiencyFileEdited[] = defFilePayloadFiles?.true;
    const listOfFalseFiles: DeficiencyFileEdited[] = defFilePayloadFiles?.false;
    checked =
      listOfTrueFiles === undefined ||
      (listOfTrueFiles !== undefined && listOfTrueFiles?.length < 5)
        ? checked
        : false;
    if (listOfTrueFiles !== undefined && listOfTrueFiles?.length > 0 && checked === true) {
      order = getFileOrder(listOfTrueFiles);
    } else if (
      listOfFalseFiles !== undefined &&
      listOfFalseFiles?.length > 0 &&
      checked === false
    ) {
      order = getFileOrder(listOfFalseFiles);
    } else if (listOfFalseFiles === undefined && checked === false) {
      order = 0;
    } else if (listOfTrueFiles === undefined && checked === true) {
      order = 0;
    }

    return order;
  };

  const updateEditDetails = async (payload: {
    fileName: string;
    fileDescription: string;
    fileShowOnReport: boolean;
  }) => {
    switch (fileData.fileCategory) {
      case FileCategory.DEFICIENCY_FILE:
        const updatedFileList: DeficiencyFileEdited[] = JSON.parse(JSON.stringify(fileList));
        updatedFileList[index].fileName = payload.fileName;
        updatedFileList[index].fileDescription = payload.fileDescription;
        updatedFileList[index].showOnReport = payload.fileShowOnReport;
        updatedFileList[index].fkInspectorField = updatedFileList[index].fkInspectorField || 0;
        if (
          updatedFileList[index]?.operation !== FileOperation.fileBlobEdited &&
          updatedFileList[index]?.operation !== FileOperation.fileCopy
        ) {
          updatedFileList[index].operation = FileOperation.fileDetailsEdited;
        }
        updatedFileList[index].fileSortOrder = handleShowOnReportBox(payload.fileShowOnReport);
        updateFileList(updatedFileList);
        break;
      case FileCategory.INSPECTION_FILE:
        await updateInspectionFile(
          customFileData.fileAimsUuid,
          payload.fileName,
          payload.fileShowOnReport,
          payload.fileDescription,
          'False',
          fileData?.fileSortOrder,
          fileData?.fkInspectorField || 0,
        );
        break;
    }
    setOpenEditDetails(false);
  };

  const downloadReport = async (filePath: string, fileName: string) => {
    setOffline(false);

    const slicedFilePath = filePath.charAt(0) === '/' ? filePath.slice(1) : filePath;
    if (isJestTest()) return;
    const result = await getImage(slicedFilePath);
    const blobPath = await fetch(String(result))
      .then(r => r.blob())
      .then(e => {
        return e;
      });
    const path = URL.createObjectURL(blobPath);
    downloadURI(path, fileName);
    setLoading(false);
  };

  const handleOnIconClick = (imageUrl: string, filePath: string, fileName: string) => {
    if (imageUrl) {
      if (['csv', 'xls', 'docx', 'mov', 'doc', 'docx'].includes(fileExtension)) {
        downloadURI(imageUrl, fileName);
      } else {
        window.open(imageUrl, '_blank');
      }
    } else {
      if (online) {
        setLoading(true);
        setOffline(false);
        downloadReport(filePath, fileName);
      } else {
        setOffline(true);
      }
    }
  };

  useEffect(() => {
    if (isNotOnDemandSync() && !isJestTest()) setFileUrlFromDb(customFileData.fileAimsUuid);
  }, [fileData, online]);

  useEffect(() => {
    if (isImage) {
      const element = document.getElementById(id);
      if (element) {
        element.style.background = `url("${fileUrl}") center`;
      }
    }
  }, [isImage, fileUrl]);

  useEffect(() => {
    if (openCopy) {
      document.getElementById(`view-image-${id}`)?.click();
      handlePopOverClose();
    }
  }, [openCopy]);

  useEffect(() => {
    if (customFileData?.fileAimsUuid === UploadFileProgress.getFileUUID) {
      setIsLoading(true);
      setUploadProgress(
        UploadFileProgress.getUploadFileProgress ? UploadFileProgress.getUploadFileProgress : 0,
      );
      if (UploadFileProgress.getUploadFileProgress === 100) {
        UploadFileProgress.setUploadValue('', 0);
        setIsLoading(false);
      }
    }
  }, [UploadFileProgress.getUploadFileProgress]);

  return (
    <>
      <Grid>
        <img
          ref={imgRef}
          src={fileUrl}
          data-testid={`view-image-${id}`}
          id={`view-image-${id}`}
          className={classes.imageBox}
          hidden={hideImage ? true : false}
          onClick={() => {
            if (
              isEditable &&
              (!openCopy ? !isActiveDeficiency || fileData?.isReInspectedFile : true)
            ) {
              showMarkerArea();
            } else {
              setOpenViewImage(true);
            }
          }}
        />

        <div id="markerOverlay" data-testid={`markerjs-dialogue-${customFileData.fileName}`} />
      </Grid>
      <Popover
        open={openPopOver}
        anchorEl={anchorEl}
        onClose={handlePopOverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        id={idForPopover}
        classes={{ paper: classes.paper }}
      >
        <Grid container className={classes.editDetailsGrid}>
          <Grid item xs={12}>
            <Button
              variant="text"
              color="white"
              noMinHeight
              onClick={handleEditDetailsOpen}
              className={classes.editDetails}
              disabled={!isEditable}
            >
              <Typography className={classes.editDetailstext}>{EDIT_DETAILS}</Typography>
            </Button>
          </Grid>
          {(!isActiveDeficiency || fileData.isReInspectedFile) && (
            <Grid item xs={12}>
              <div>
                <Button
                  variant="text"
                  color="white"
                  noMinHeight
                  onClick={handleDeleteOpen}
                  className={classes.editDetails}
                  disabled={!isEditable}
                >
                  <Typography className={classes.editDetailstext}>{DELETE}</Typography>
                </Button>
              </div>
            </Grid>
          )}
          {isImage && (
            <Grid item container xs={12}>
              <Button
                variant="text"
                color="white"
                noMinHeight
                className={classes.editDetails}
                onClick={() => {
                  setOpenCopy(true);
                  document.body.style.overflow = 'hidden';
                }}
                disabled={!isEditable}
              >
                <Typography className={classes.editDetailstext}>{COPY}</Typography>
              </Button>
            </Grid>
          )}
        </Grid>
      </Popover>

      {isLoading && UploadFileProgress.getFileUUID && !isJestTest() ? (
        <Grid
          item
          container
          direction="column"
          justifyContent="center"
          alignItems="center"
          xs={12}
          className={classes.loadingGrid}
        >
          <AspectRatio ratio={0.6}>
            <Grid className={classes.uploadingGrid}>
              <Typography className={classes.progressText} variant="h3">
                {uploadProgress}%
              </Typography>
              <Typography className={classes.uploadText} variant="h6">
                Uploading
              </Typography>
            </Grid>

            <div className={classes.progressBar}>
              <LinearProgress variant="determinate" value={0} />
            </div>
          </AspectRatio>
        </Grid>
      ) : (
        <>
          <Grid
            item
            container
            direction="column"
            justifyContent="center"
            alignItems="center"
            xs={12}
          >
            <div
              className={`${classes.photoCardAdd} ${
                fileDownloadError || fileNotFound ? classes.fileError : ''
              }`}
            >
              <AspectRatio ratio={0.6}>
                {!fileNotFound ? (
                  isImage ? (
                    <>
                      {!fileDownloadError ? (
                        <Box
                          className={classes.thumbnail}
                          id={id}
                          onClick={() => {
                            document.getElementById(`view-image-${id}`)?.click();
                          }}
                        ></Box>
                      ) : (
                        <div className={classes.fileDiv}>
                          <CircularProgress />
                        </div>
                      )}
                    </>
                  ) : (
                    <div className={classes.fileDiv}>
                      {fileExtension && (
                        <>
                          {!loading && !fileDownloadError ? (
                            <img
                              src={`${getFileDataBasedOnType(fileExtension).src}`}
                              alt={`${getFileDataBasedOnType(fileExtension).alt}`}
                              onClick={() => {
                                handleOnIconClick(
                                  fileUrl,
                                  customFileData.originalPath,
                                  customFileData.fileName,
                                );
                              }}
                              data-testid={customFileData.fileName}
                            />
                          ) : (
                            <CircularProgress />
                          )}
                        </>
                      )}
                    </div>
                  )
                ) : (
                  <div className={`${classes.fileDiv}`}>
                    <h3>File Not Available</h3>
                  </div>
                )}
                <div className={!isEditable ? classes.hideDiv : classes.metadata2}>
                  <div
                    aria-describedby={idForPopover}
                    onClick={handlePopOverOpen}
                    className={classes.verticalFeather}
                    data-testid={`more-options-file-${customFileData.fileName}`}
                  >
                    <Icons.MoreVerticalFeather color="primary" />
                  </div>
                </div>

                {fileData.isReInspectedFile && (
                  <div className={classes.newFileTag}>
                    <Typography>New</Typography>
                  </div>
                )}

                <div className={classes.metadata}>
                  <Typography className={classes.fileName}>
                    {customFileData.fileName && customFileData.fileName.length > 60 ? (
                      <>
                        <Tooltip title={customFileData.fileName}>
                          <>
                            {customFileData.fileName.substring(0, 60)}
                            ...
                          </>
                        </Tooltip>
                      </>
                    ) : (
                      customFileData.fileName
                    )}
                  </Typography>
                  <Grid>
                    {fileData.fileDescription && fileData.fileDescription.length > 60 ? (
                      <>
                        <Typography className={classes.fileDescription}>
                          <Tooltip title={fileData.fileDescription}>
                            <>
                              {fileData.fileDescription.substring(0, 60)}
                              ...
                              <Typography className={classes.readMore} color="primary">
                                Read more
                              </Typography>
                            </>
                          </Tooltip>
                        </Typography>
                      </>
                    ) : (
                      fileData.fileDescription
                    )}
                  </Grid>
                  <Grid>{`Date Added: ${dayjs(fileData?.dateCreated).format(
                    YYYYMMDD_DATE_FORMAT,
                  )}`}</Grid>
                  <FormControlLabel
                    className={classes.showOnReport}
                    control={
                      <Checkbox
                        onChange={e => {
                          updateEditDetails({
                            fileName: customFileData.fileName,
                            fileDescription: fileData.fileDescription,
                            fileShowOnReport: e.target.checked,
                          });
                        }}
                        checked={fileData.showOnReport}
                        inputProps={{ 'aria-label': 'controlled' }}
                        disabled={
                          !isEditable || (isShowOnReport && fileData.showOnReport === false)
                            ? true
                            : false
                        }
                      />
                    }
                    label="Show on report"
                  />
                </div>
              </AspectRatio>
            </div>
          </Grid>
        </>
      )}

      {openFileDelete && (
        <Dialog
          onClose={() => setOpenFileDelete(false)}
          open={openFileDelete}
          titleText="Delete File"
          backButton={false}
          dialogActions={
            <Grid
              container
              justifyContent="flex-end"
              alignItems="center"
              className={classes.dialogPadding}
            >
              <Grid item>
                <Button
                  noMinHeight
                  color="error"
                  variant="contained"
                  onClick={handleFileDelete}
                  data-testid="delete-file"
                >
                  Yes, Delete
                </Button>
              </Grid>
            </Grid>
          }
          maxWidth="xs"
          noMinHeight
        >
          <Grid container className={classes.dialogContentPadding}>
            <Grid item container xs={12} alignItems="center">
              <Typography className={classes.deleteText}>
                {`Are you sure want to delete "${customFileData.fileName}"?`}
              </Typography>
            </Grid>
          </Grid>
        </Dialog>
      )}
      {openEditDetails && (
        <EditFileDetails
          handleEditDetailsClose={handleEditDetailsClose}
          openEditDetails={openEditDetails}
          isShowOnReport={!isEditable || (isShowOnReport && fileData.showOnReport === false)}
          fileData={{
            fileName: customFileData.fileName,
            fileDescription: fileData.fileDescription,
            fileShowOnReport: fileData.showOnReport,
            originalFileName: fileData.originalFileName,
            extension: fileData?.extension,
          }}
          updateEditDetails={updateEditDetails}
        />
      )}
      {openViewImage && (
        <Dialog
          open={openViewImage}
          onClose={() => setOpenViewImage(false)}
          backButton={false}
          titleText={`${name}`}
          fullScreen
          maxWidth="xs"
          noMinHeight
          dialogActions={
            <Grid
              container
              justifyContent="flex-end"
              alignItems="center"
              className={classes.closeButtonGrid}
            >
              <Grid item>
                <Button
                  noMinHeight
                  color="primary"
                  variant="contained"
                  onClick={() => setOpenViewImage(false)}
                >
                  Close
                </Button>
              </Grid>
            </Grid>
          }
        >
          <Grid container className={classes.viewImage}>
            <img alt={customFileData.fileName} className={classes.viewFullImage} src={fileUrl} />
          </Grid>
        </Dialog>
      )}
      {offline && <OfflineDialog open={offline} onClose={() => setOffline(false)} />}
    </>
  );
};

export default observer(Thumbnail);
